/**
 * 逻辑执行引擎
 * 支持 SHOW/HIDE/JUMP/REQUIRE/SELECT_OPTION/SHOW_OPTION 六种逻辑类型
 * 支持 SELECTED/NOT_SELECTED/EMPTY/NOT_EMPTY/SHOWN/NOT_SHOWN/EQUALS/CONTAINS 等条件类型
 */

/**
 * 检查条件是否满足
 * @param {string} conditionType - 条件类型
 * @param {string} conditionValue - 条件值
 * @param {*} answerValue - 用户答案值
 * @param {Object} context - 上下文（包含题目可见性等信息）
 */
const checkCondition = (conditionType, conditionValue, answerValue, context = {}) => {
    // 处理 SHOWN/NOT_SHOWN 条件（不依赖答案值）
    if (conditionType === 'SHOWN') {
        const itemKey = context.sourceItemKey
        return itemKey && !context.hidden?.has(itemKey)
    }
    if (conditionType === 'NOT_SHOWN') {
        const itemKey = context.sourceItemKey
        return itemKey && context.hidden?.has(itemKey)
    }

    // 处理 EMPTY/NOT_EMPTY 条件
    if (conditionType === 'EMPTY' || conditionType === 'not_answered') {
        if (answerValue == null) return true
        if (Array.isArray(answerValue)) return answerValue.length === 0 || answerValue.every(v => !v)
        return answerValue === '' || answerValue === undefined
    }
    if (conditionType === 'NOT_EMPTY' || conditionType === 'answered') {
        if (answerValue == null) return false
        if (Array.isArray(answerValue)) return answerValue.some(v => !!v)
        return answerValue !== '' && answerValue !== undefined
    }

    // 其他条件需要有答案值
    if (answerValue == null) return false

    const answer = Array.isArray(answerValue) ? answerValue : [answerValue]
    const expected = conditionValue ? conditionValue.split(',') : []

    switch (conditionType) {
        // 选项类条件
        case 'SELECTED':
        case 'selected':
            // 支持 answer 是数字数组，expected 是字符串数组的情况
            return expected.some(v =>
                answer.includes(v) ||
                answer.includes(String(v)) ||
                answer.includes(Number(v))
            )

        case 'NOT_SELECTED':
        case 'not_selected':
            return !expected.some(v =>
                answer.includes(v) ||
                answer.includes(String(v)) ||
                answer.includes(Number(v))
            )

        // 精确匹配
        case 'EQUALS':
        case 'eq':
            if (Array.isArray(answerValue)) {
                return answer.includes(conditionValue) || answer.includes(String(conditionValue))
            }
            return String(answerValue) === String(conditionValue)

        case 'NOT_EQUALS':
            if (Array.isArray(answerValue)) {
                return !answer.includes(conditionValue) && !answer.includes(String(conditionValue))
            }
            return String(answerValue) !== String(conditionValue)

        // 包含匹配
        case 'CONTAINS':
            return expected.some(v => answer.includes(v) || answer.includes(String(v)))

        case 'NOT_CONTAINS':
            return !expected.some(v => answer.includes(v) || answer.includes(String(v)))

        // 数值比较
        case 'GREATER_THAN':
        case 'gt':
            return Number(answerValue) > Number(conditionValue)

        case 'GREATER_THAN_OR_EQUAL':
        case 'gte':
            return Number(answerValue) >= Number(conditionValue)

        case 'LESS_THAN':
        case 'lt':
            return Number(answerValue) < Number(conditionValue)

        case 'LESS_THAN_OR_EQUAL':
        case 'lte':
            return Number(answerValue) <= Number(conditionValue)

        default:
            console.warn('Unknown condition type:', conditionType)
            return false
    }
}

/**
 * 解析动作配置
 * @param {string} actionConfig - JSON格式的动作配置
 */
const parseActionConfig = (actionConfig) => {
    if (!actionConfig) return {}
    try {
        return JSON.parse(actionConfig)
    } catch (e) {
        console.warn('Failed to parse actionConfig:', actionConfig)
        return {}
    }
}

/**
 * 评估所有逻辑规则
 * @param {Array} rules - 逻辑规则列表
 * @param {Object} answers - 当前答案 { itemKey: value }
 * @returns {{ 
 *   hidden: Set,           // 隐藏的题目集合
 *   required: Set,         // 必填的题目集合
 *   jumpTo: string|null,   // 跳转目标题目key
 *   selectedOptions: Map,  // 自动选中的选项 { itemKey: optionValue }
 *   shownOptions: Map,     // 显示的选项 { itemKey: Set<optionValue> }
 *   hiddenOptions: Map     // 隐藏的选项 { itemKey: Set<optionValue> }
 * }}
 */
export const evaluateLogic = (rules, answers, externalAnswers = {}) => {
    const hidden = new Set()
    const required = new Set()
    const selectedOptions = new Map()
    const shownOptions = new Map()
    const hiddenOptions = new Map()
    let jumpTo = null
    let endType = null  // 'NORMAL_END' | 'EARLY_END' | null

    if (!rules || !Array.isArray(rules)) {
        return { hidden, required, jumpTo, endType, selectedOptions, shownOptions, hiddenOptions }
    }

    // 预处理：收集所有SHOW规则的目标题目，这些题目应该默认隐藏
    // 只有当条件满足时才显示
    // 同时处理SHOW_OPTION规则：选项也应该默认隐藏
    rules.forEach(rule => {
        const logicType = rule.logic_type || rule.logicType
        const targetItemKey = rule.target_item_key || rule.targetItemKey
        const actionConfig = rule.action_config || rule.actionConfig
        const logicTypeUpper = (logicType || '').toUpperCase()

        // SHOW规则：题目默认隐藏
        if (logicTypeUpper === 'SHOW' && targetItemKey) {
            hidden.add(targetItemKey)
        }

        // SHOW_OPTION规则：选项默认隐藏
        if (logicTypeUpper === 'SHOW_OPTION' && targetItemKey) {
            const parsedConfig = parseActionConfig(actionConfig)
            if (parsedConfig.targetOptionValue != null) {
                if (!hiddenOptions.has(targetItemKey)) {
                    hiddenOptions.set(targetItemKey, new Set())
                }
                // 处理数组和单值
                const optionValues = Array.isArray(parsedConfig.targetOptionValue)
                    ? parsedConfig.targetOptionValue
                    : [parsedConfig.targetOptionValue]
                optionValues.forEach(v => hiddenOptions.get(targetItemKey).add(String(v)))
            }
        }
    })


    // 创建上下文对象，用于 SHOWN/NOT_SHOWN 条件判断
    const context = {
        hidden: hidden,
        sourceItemKey: null,
        currentFormKey: (rules[0] && (rules[0].form_key || rules[0].formKey)) || null
    }


    rules.forEach(rule => {
        // 兼容不同的字段命名格式（驼峰和下划线）
        const sourceItemKey = rule.source_item_key || rule.sourceItemKey
        const conditionType = rule.condition_type || rule.conditionType
        const conditionValue = rule.condition_value || rule.conditionValue
        const logicType = rule.logic_type || rule.logicType
        const targetItemKey = rule.target_item_key || rule.targetItemKey
        const actionConfig = rule.action_config || rule.actionConfig

        // 解析actionConfig获取条件组
        const parsedConfig = parseActionConfig(actionConfig)
        const conditionGroup = parsedConfig.conditionGroup

        let conditionMet = false

        if (conditionGroup && conditionGroup.conditions && conditionGroup.conditions.length > 0) {
            // 多条件判断
            const operator = conditionGroup.operator || 'AND'
            const conditionResults = conditionGroup.conditions.map(cond => {
                context.sourceItemKey = cond.sourceItemKey
                const sourceFormKey = cond.sourceFormKey
                const isExternal = !!sourceFormKey && !!context.currentFormKey && sourceFormKey !== context.currentFormKey
                let condValue = isExternal
                    ? externalAnswers?.[sourceFormKey]?.[cond.sourceItemKey]
                    : answers[cond.sourceItemKey]

                if (isExternal && Array.isArray(cond.optionMapping) && cond.optionMapping.length > 0) {
                    const mapObj = {}
                    cond.optionMapping.forEach(m => {
                        if (m && m.fromValue != null && m.toValue != null) mapObj[String(m.fromValue)] = m.toValue
                    })
                    if (Array.isArray(condValue)) {
                        condValue = condValue.map(v => (mapObj[String(v)] != null ? mapObj[String(v)] : v))
                    } else if (condValue != null && mapObj[String(condValue)] != null) {
                        condValue = mapObj[String(condValue)]
                    }
                }

                const result = checkCondition(cond.conditionType, cond.conditionValue, condValue, context)
                return result
            })

            if (operator === 'OR') {
                // 或：任意条件满足即可
                conditionMet = conditionResults.some(result => result === true)
            } else {
                // 且：所有条件都必须满足
                conditionMet = conditionResults.every(result => result === true)
            }
        } else {
            // 单条件判断（兼容旧格式）
            context.sourceItemKey = sourceItemKey
            const value = answers[sourceItemKey]
            conditionMet = checkCondition(conditionType, conditionValue, value, context)
        }

        if (conditionMet) {
            const logicTypeUpper = (logicType || '').toUpperCase()

            switch (logicTypeUpper) {
                case 'HIDE':
                    hidden.add(targetItemKey)
                    break

                case 'SHOW':
                    hidden.delete(targetItemKey)
                    break

                case 'JUMP':
                    // 特殊跳转目标：正常结束 / 提前结束
                    const targetUpper = (targetItemKey || '').toUpperCase()
                    const targetText = targetItemKey || ''

                    // 正常结束（计入结果）
                    if (targetUpper === '__END__' ||
                        targetUpper === '__NORMAL_END__' ||
                        targetUpper === 'NORMAL_END' ||
                        targetText === '正常结束' ||
                        targetText.includes('正常结束') ||
                        targetText.includes('计入结果')) {
                        endType = 'NORMAL_END'
                    }
                    // 提前结束（不计入结果）
                    else if (targetUpper === '__EARLY_END__' ||
                        targetUpper === 'EARLY_END' ||
                        targetText === '提前结束' ||
                        targetText.includes('提前结束') ||
                        targetText.includes('不计入结果')) {
                        endType = 'EARLY_END'
                    }
                    // 普通跳转到题目
                    else {
                        jumpTo = targetItemKey
                    }
                    break

                case 'REQUIRE':
                    required.add(targetItemKey)
                    break

                case 'SELECT_OPTION':
                    // 自动选中目标题目的某个选项
                    const selectConfig = parseActionConfig(actionConfig)
                    if (selectConfig.targetOptionValue != null) {
                        selectedOptions.set(targetItemKey, selectConfig.targetOptionValue)
                    }
                    break

                case 'SHOW_OPTION':
                    // 显示目标题目的某个选项：从hiddenOptions中移除（预处理时已添加到hiddenOptions）
                    const showConfig = parseActionConfig(actionConfig)
                    if (showConfig.targetOptionValue != null) {
                        // 处理数组和单值
                        const showValues = Array.isArray(showConfig.targetOptionValue)
                            ? showConfig.targetOptionValue
                            : [showConfig.targetOptionValue]
                        // 从hiddenOptions中移除这些选项
                        if (hiddenOptions.has(targetItemKey)) {
                            showValues.forEach(v => hiddenOptions.get(targetItemKey).delete(String(v)))
                        }
                    }
                    break


                case 'HIDE_OPTION':
                    // 隐藏目标题目的某个选项
                    const hideConfig = parseActionConfig(actionConfig)
                    if (hideConfig.targetOptionValue != null) {
                        if (!hiddenOptions.has(targetItemKey)) {
                            hiddenOptions.set(targetItemKey, new Set())
                        }
                        // 处理数组和单值
                        const hideValues = Array.isArray(hideConfig.targetOptionValue)
                            ? hideConfig.targetOptionValue
                            : [hideConfig.targetOptionValue]
                        hideValues.forEach(v => hiddenOptions.get(targetItemKey).add(String(v)))
                    }
                    break


                default:
                    console.warn('Unknown logic type:', logicType)
            }
        }
    })

    return { hidden, required, jumpTo, endType, selectedOptions, shownOptions, hiddenOptions }
}


/**
 * 过滤可见的选项（根据显示/隐藏选项逻辑）
 * @param {string} itemKey - 题目key
 * @param {Array} options - 原始选项列表
 * @param {Map} shownOptions - 显示的选项映射
 * @param {Map} hiddenOptions - 隐藏的选项映射
 */
export const filterVisibleOptions = (itemKey, options, shownOptions, hiddenOptions) => {
    if (!options || options.length === 0) return options

    const shownSet = shownOptions?.get(itemKey)
    const hiddenSet = hiddenOptions?.get(itemKey)

    return options.filter(opt => {
        const optValue = opt.value || opt.optionKey || opt.id

        // 如果有显示选项设置，只显示在集合中的选项
        if (shownSet && shownSet.size > 0) {
            return shownSet.has(optValue) || shownSet.has(String(optValue))
        }

        // 如果有隐藏选项设置，隐藏在集合中的选项
        if (hiddenSet && hiddenSet.size > 0) {
            return !hiddenSet.has(optValue) && !hiddenSet.has(String(optValue))
        }

        return true
    })
}

export default { evaluateLogic, checkCondition, filterVisibleOptions }
